// @flow

import { PrivateStateAccessor } from '@ta/common.state';

export type Entrypoints =
  | 'UNKNOWN'
  | 'BRAND_EDUCATOR'
  | 'MASTHEAD'
  | 'GOOGLE_ONETAP'
  | 'GOOGLE_ONETAP_NON_HOME'
  | 'TA_TWOTAP'
  | 'TA_TWOTAP_NON_HOME'
  | 'FACEBOOK_ONETAP'
  | 'FACEBOOK_ONETAP_NON_HOME'
  | 'FACEBOOK_ENGAGEMENT_CARD'
  /* when a user signs up via SUAS promo banner */
  | 'SIGN_UP_AND_SAVE'
  /* when a user signs in via SUAS promo banner */
  | 'SIGN_IN_AND_SAVE'
  | 'HOMEPAGE_LEFT_RAIL';

// These entrypoints go straight to confirm username
export const CONFIRM_USERNAME_ENTRYPOINTS: Set<Entrypoints> = new Set([
  'MASTHEAD',
  'GOOGLE_ONETAP',
  'GOOGLE_ONETAP_NON_HOME',
  'TA_TWOTAP',
  'TA_TWOTAP_NON_HOME',
  'FACEBOOK_ONETAP',
  'FACEBOOK_ONETAP_NON_HOME',
  'FACEBOOK_ENGAGEMENT_CARD',
  'HOMEPAGE_LEFT_RAIL',
]);

// Define the state
export type OnboardingState = {
  shouldFire: boolean,
  immediateLoad: boolean,
  entryPoint: Entrypoints,
};

// Define the key and the default
// The key should be unique to your package
const UNKNOWN_ONBOARDING_ENTRY_POINT = 'UNKNOWN';
const KEY = 'onboarding-state';
const DEFAULT_STATE = {
  shouldFire: false,
  immediateLoad: false,
  entryPoint: UNKNOWN_ONBOARDING_ENTRY_POINT,
};

export const STATE_ACCESS: PrivateStateAccessor<OnboardingState> = new PrivateStateAccessor(
  KEY,
  DEFAULT_STATE,
);

// Export the method that fetches the state.
export const getState = (state: any) => STATE_ACCESS.getFrom(state);

const SET_FIRE_ONBOARDING = 'FIRE_ONBOARDING';
const RESET_FIRE_ONBOARDING = 'RESET';

// Type def for the actions of our slice
export type OnboardingActions = {
  setShouldFire: (immediateLoad?: boolean, entryPoint?: Entrypoints) => void,
  resetShouldFire: () => void,
};

// Export your actions so that other modules can dispatch them
// STATE_ACCESS.action(...) turns a private action creator into a global action creator
// that will send actions to your private slice reducer

export const ACTIONS: OnboardingActions = {
  setShouldFire: (immediateLoad, entryPoint) =>
    STATE_ACCESS.action({
      type: SET_FIRE_ONBOARDING,
      immediateLoad: immediateLoad || false,
      entryPoint: entryPoint || UNKNOWN_ONBOARDING_ENTRY_POINT,
    }),
  resetShouldFire: () =>
    STATE_ACCESS.action({
      type: RESET_FIRE_ONBOARDING,
    }),
};

// Set the reducer for the private slice.
STATE_ACCESS.setReducer((prevState: OnboardingState, action: any) => {
  switch (action.type) {
    case SET_FIRE_ONBOARDING:
      return {
        shouldFire: true,
        immediateLoad: action.immediateLoad,
        entryPoint: action.entryPoint,
      };
    case RESET_FIRE_ONBOARDING:
      return {
        shouldFire: false,
        immediateLoad: false,
        entryPoint: UNKNOWN_ONBOARDING_ENTRY_POINT,
      };
    default:
      return prevState;
  }
});

if (LEGACY_AMD_SUPPORT) {
  // eslint-disable-next-line
  define('@ta/social.onboarding-state', [], () =>
    __webpack_require__('@ta/social.onboarding-state'));
}
